import { useEffect, useState } from 'react';
import type { Project } from '../domain/portfolio.types';

interface ProjectDialogProps {
  project: Project | null;
  onClose: () => void;
}

export function ProjectDialog({ project, onClose }: ProjectDialogProps) {
  const [fullImg, setFullImg] = useState<string | null>(null);

  useEffect(() => {
    if (project) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [project]);

  if (!project) return null;

  return (
    <div className="project-dialog-backdrop" onClick={onClose}>
      <div className="project-dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="project-dialog-sticky-header">
          <div className="pd-header-inner">
            <span className="pd-header-label">{project.title}</span>
            <button className="project-dialog-close" onClick={onClose} aria-label="Close dialog">
              <span className="close-label">Close</span>
              <span className="close-icon">✕</span>
            </button>
          </div>
        </div>
        
        <div className="project-dialog-scroll-body">
        
        <div className="project-dialog-header">
          <span className="label">{project.meta?.label}</span>
          <h2 className="title">{project.title}</h2>
          <div className="tags">
            {project.tags.map((t, i) => <span key={i} className="project-tag">{t}</span>)}
          </div>
        </div>

        <div className="project-dialog-body">
          {project.details?.images && project.details.images.length > 0 && (
            <div className="project-gallery">
              {project.details.images.map((img, i) => (
                <img 
                  key={i} 
                  src={img} 
                  alt={`${project.title} screenshot ${i + 1}`} 
                  className="project-img" 
                  onClick={() => setFullImg(img)}
                  style={{ cursor: 'zoom-in' }}
                />
              ))}
            </div>
          )}

          <div className="project-desc">
            <p className="project-desc-text">{project.details?.description || project.blurb}</p>
            {project.details?.highlights && project.details.highlights.length > 0 && (
              <div>
                <h4 className="pd-highlights-title">Key Highlights</h4>
                <ul className="pd-highlights-list">
                  {project.details.highlights.map((hl, i) => (
                    <li key={i} className="pd-highlight-item">
                      <span className="dot">▹</span>
                      <span className="text">{hl}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="project-links">
            {project.details?.links?.github && (
              <a href={project.details.links.github} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                </svg>
                GitHub
              </a>
            )}
            {project.details?.links?.demo && (
              <a href={project.details.links.demo} target="_blank" rel="noopener noreferrer" className="btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                Live Demo
              </a>
            )}
          </div>
        </div>
        </div>
      </div>

      {fullImg && (
        <div className="lightbox-backdrop" onClick={(e) => { e.stopPropagation(); setFullImg(null); }}>
          <button className="lightbox-close" onClick={(e) => { e.stopPropagation(); setFullImg(null); }}>✕</button>
          <img src={fullImg} alt="Full size" className="lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
